import { ArrowForwardIos, KeyboardArrowDown, KeyboardArrowUp, Logout } from '@mui/icons-material';
import { Avatar, Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { styled, Theme } from '@mui/material/styles';
import type { GetTenantsForEmailQuery } from '__generated__/graphql';
import { TimezoneDisplayPreference } from '__generated__/graphql';
import { useAuthContext } from 'context/auth.context';
import {
  useDisplayTimezoneContext,
  type DisplayTimezoneMode,
} from 'context/displayTimezone.context';
import useGetTenantsForCurrentUser from 'hooks/auth/queries/useGetTenantsForCurrentUser';
import React, { useCallback, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PermissionCheck from '../../components/Abilities/PermissionCheck';
import { privateRoutes } from '../../router/routes';
import { getUniqueKey } from '../../utils';
import { useMobileOnlyLinks } from '../layoutLinks';

import CheckIcon from '@mui/icons-material/Check';
import ExternalLinkIcon from 'components/Icon/ExternalLinkIcon';
import TenancyModal from 'components/TenancyModal/TenancyModal';
import useUpdateUserSettingsMutation from 'hooks/users/mutations/useUpdateUserSettingsMutation';

const settings: string[] = [];

const TIMEZONE_OPTIONS: { value: TimezoneDisplayPreference; label: string }[] = [
  { value: TimezoneDisplayPreference.Utc, label: 'UTC' },
  { value: TimezoneDisplayPreference.Tenant, label: 'Tenant' },
  { value: TimezoneDisplayPreference.User, label: 'Users' },
  { value: TimezoneDisplayPreference.Locale, label: 'Locale' },
];

const userDropdownPaperSx = (theme: Theme) => ({
  borderRadius: '.5rem',
  backgroundColor: theme.colors.neutral700,
  color: theme.colors.white,
  '& .MuiMenuItem-root': {
    paddingTop: '.625rem',
    paddingBottom: '.75rem',
    fontWeight: 400,
    color: theme.colors.white,
    textDecoration: 'none',
    '&:hover': {
      backgroundColor: theme.colors.menuHover,
      color: theme.colors.white,
    },
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
      color: theme.colors.white,
      marginRight: '0.625rem',
    },
    '& .MuiListItemText-primary': {
      color: theme.colors.white,
    },
  },
});

const userDropdownSlotProps = {
  paper: {
    sx: userDropdownPaperSx,
  },
};

const UserMenuWrapper = styled(Box)<{ $canSwitchTenant: boolean }>`
  display: flex;
  align-items: center;
  margin-left: 1rem;
  padding: 0.25rem;

  border: 1px solid
    ${({ $canSwitchTenant, theme }) =>
      $canSwitchTenant ? theme.colors.userMultitenantHighlight : theme.colors.headerBackground};
  border-radius: 2rem;

  &:hover {
    background-color: ${({ theme, $canSwitchTenant }) =>
      $canSwitchTenant ? theme.colors.headerBackgroundHover : 'inherit'};
  }

  & svg {
    color: ${({ theme }) => theme.colors.white};
  }
`;

const TenantName = styled('span')`
  font-size: 1rem;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  padding-left: 0.5rem;
  padding-right: 0.625rem;
  color: ${({ theme }) => theme.colors.userMultitenantHighlight};
`;

const UserMenu = () => {
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const [userMenuOpen, setUserMenuOpen] = useState<boolean>(false);
  const { logout, user, setUser, userTenantId, userTenantName, userTenantOnLogin } =
    useAuthContext();
  const { displayTimezoneMode, setDisplayTimezoneMode } = useDisplayTimezoneContext();
  const mobileOnlyLinks = useMobileOnlyLinks();
  const [tenancyMenuRef, setTenancyMenuRef] = useState<null | HTMLElement>(null);
  const [timezoneMenuRef, setTimezoneMenuRef] = useState<null | HTMLElement>(null);
  const [updateUserSettings] = useUpdateUserSettingsMutation();
  const location = useLocation();
  const { tenants } = useGetTenantsForCurrentUser();
  const canSwitchTenant = useMemo(() => !!tenants && tenants.length > 1, [tenants]);

  const [selectedTenant, setSelectedTenant] = useState<
    GetTenantsForEmailQuery['getTenantsForEmail'][number] | null
  >(null);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setSelectedTenant(null);
    setTenancyMenuRef(null);
    setTimezoneMenuRef(null);
    setAnchorElUser(event.currentTarget);
    setUserMenuOpen(true);
  };

  const handleCloseUserMenu = () => {
    setSelectedTenant(null);
    setTenancyMenuRef(null);
    setTimezoneMenuRef(null);
    setAnchorElUser(null);
    setUserMenuOpen(false);
  };

  const handleChooseTenant = useCallback(
    (
      tenant: GetTenantsForEmailQuery['getTenantsForEmail'][number],
      event: React.SyntheticEvent
    ) => {
      event.stopPropagation();
      event.preventDefault();
      setSelectedTenant(tenant);
    },
    []
  );

  const handleToggleTenancyMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (tenancyMenuRef) {
        setTenancyMenuRef(null);
      } else {
        setTenancyMenuRef(event.currentTarget);
      }
      setSelectedTenant(null);
    },
    [tenancyMenuRef]
  );

  const handleCloseTenancyMenu = useCallback(() => {
    setSelectedTenant(null);
    setTenancyMenuRef(null);
  }, []);

  const handleToggleTimezoneMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (timezoneMenuRef) {
        setTimezoneMenuRef(null);
      } else {
        setTimezoneMenuRef(event.currentTarget);
      }
    },
    [timezoneMenuRef]
  );

  const handleCloseTimezoneMenu = useCallback(() => {
    setTimezoneMenuRef(null);
  }, []);

  const handleChooseTimezoneDisplayPreference = useCallback(
    (value: DisplayTimezoneMode, event: React.SyntheticEvent) => {
      event.stopPropagation();
      event.preventDefault();
      setTimezoneMenuRef(null);
      if (!user?.user) {
        return;
      }
      const previousValue = displayTimezoneMode;
      setDisplayTimezoneMode(value);
      updateUserSettings({
        variables: {
          input: {
            id: user.user.id,
            emailNotifications: !!user.user.emailNotifications,
            mobilePushNotifications: !!user.user.mobilePushNotifications,
            timezone: user.user.timezone ?? undefined,
            timezoneDisplayPreference: value,
          },
        },
        onCompleted: (result) => {
          if (!result.updateUserSettings.success) {
            setDisplayTimezoneMode(previousValue);
            return;
          }
          const session = { ...user, user: { ...user.user, timezoneDisplayPreference: value } };
          localStorage.setItem('restore.user', JSON.stringify(session));
          setUser(session);
        },
        onError: (err) => {
          setDisplayTimezoneMode(previousValue);
          console.error('Failed to save timezone preference', err);
        },
      });
    },
    [displayTimezoneMode, setDisplayTimezoneMode, setUser, updateUserSettings, user]
  );

  const tenantName = useMemo(() => {
    return userTenantOnLogin?.name ?? userTenantName;
  }, [userTenantName, userTenantOnLogin?.name]);

  return (
    <>
      <UserMenuWrapper $canSwitchTenant={canSwitchTenant}>
        <Box
          data-testid='navbar-tenant-dropdown'
          onClick={handleOpenUserMenu}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <TenantName>{tenantName}</TenantName>
          <IconButton data-testid='navbar-initial-icon' sx={{ p: 0 }}>
            <Avatar
              sx={{
                bgcolor: (theme) => theme.colors.blue300,
                color: 'black',
                fontSize: '0.875rem',
                fontWeight: 500,
                width: '2.1875rem',
                height: '2.1875rem',
              }}
            >
              {user?.user.firstName[0]}
              {user?.user.lastName[0]}
            </Avatar>
          </IconButton>
          {userMenuOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        </Box>
        <Menu
          sx={{ mt: '3rem' }}
          slotProps={userDropdownSlotProps}
          anchorEl={anchorElUser}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorElUser)}
          disableScrollLock={true}
          onClose={handleCloseUserMenu}
        >
          {canSwitchTenant && (
            <MenuItem sx={{ pr: 0 }} onClick={handleToggleTenancyMenu}>
              <ListItemText primary='Tenancy' />
              <ListItemIcon>
                <ArrowForwardIos />
              </ListItemIcon>
              <Menu
                slotProps={userDropdownSlotProps}
                open={Boolean(tenancyMenuRef)}
                anchorEl={tenancyMenuRef}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                onClose={handleCloseTenancyMenu}
              >
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.id} onClick={handleChooseTenant.bind(null, tenant)}>
                    <ListItemText primary={tenant.name} />
                    <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                      <CheckIcon
                        style={{
                          visibility:
                            tenant.id === (userTenantOnLogin?.id ?? userTenantId)
                              ? 'visible'
                              : 'hidden',
                        }}
                      />
                    </ListItemIcon>
                  </MenuItem>
                ))}
              </Menu>
            </MenuItem>
          )}
          <MenuItem
            sx={{ pr: 0 }}
            onClick={handleToggleTimezoneMenu}
            data-testid='navbar-timezone-preference'
          >
            <ListItemText primary='Time Zone' />
            <ListItemIcon>
              <ArrowForwardIos />
            </ListItemIcon>
            <Menu
              slotProps={userDropdownSlotProps}
              open={Boolean(timezoneMenuRef)}
              anchorEl={timezoneMenuRef}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              onClose={handleCloseTimezoneMenu}
            >
              {TIMEZONE_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value}
                  onClick={(e) => handleChooseTimezoneDisplayPreference(option.value, e)}
                  data-testid={`navbar-timezone-preference-${option.value}`}
                >
                  <ListItemText primary={option.label} />
                  <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                    <CheckIcon
                      style={{
                        visibility: displayTimezoneMode === option.value ? 'visible' : 'hidden',
                      }}
                    />
                  </ListItemIcon>
                </MenuItem>
              ))}
            </Menu>
          </MenuItem>
          {settings?.map((setting) => (
            <MenuItem key={setting} onClick={handleCloseUserMenu}>
              <ListItemText primary={setting} />
            </MenuItem>
          ))}
          {mobileOnlyLinks?.map((sideNavLink) => (
            <PermissionCheck
              key={getUniqueKey(sideNavLink)}
              permission={
                privateRoutes.find((route) => route.path === sideNavLink.path)?.permission
              }
            >
              <MenuItem
                data-testid={sideNavLink.testId}
                component={Link}
                to={sideNavLink.path}
                selected={location.pathname.includes(sideNavLink.path)}
                onClick={() => handleCloseUserMenu()}
                target={sideNavLink.external ? '_blank' : '_self'}
              >
                <ListItemText
                  primary={sideNavLink.title}
                  primaryTypographyProps={
                    location.pathname.includes(sideNavLink.path)
                      ? { style: { fontWeight: 500 } }
                      : {}
                  }
                  sx={{ color: 'white', marginRight: sideNavLink.external ? '2rem' : undefined }}
                />
                {sideNavLink.external && <ExternalLinkIcon fill='white' width={14} height={14} />}
              </MenuItem>
            </PermissionCheck>
          ))}
          <MenuItem
            data-testid='navbar-log-out'
            onClick={() => logout()}
            // sx={{ mt: 1.25 }} // Not necessary if while settings array is empty
          >
            <ListItemIcon>
              <Logout />
            </ListItemIcon>
            <ListItemText primary={'Log Out'} />
          </MenuItem>
        </Menu>
      </UserMenuWrapper>
      {selectedTenant && (
        <TenancyModal
          tenantId={selectedTenant.id}
          tenantName={selectedTenant.name}
          open={!!selectedTenant}
          onClose={handleCloseUserMenu}
        />
      )}
    </>
  );
};

export default UserMenu;
